// Activo Fijo → Valorización → Revisión de vida útil (NIC 16 §51).
//
// La norma obliga a revisar cada cierre la vida útil y el valor residual. El
// trabajo hay que hacerlo igual; lo que faltaba era el papel que lo prueba.
//
// La revisión PROPONE a quién mirar en vez de listar los 800 activos: un
// listado completo no se revisa, se firma sin leer, y entonces el acta no vale
// nada. Cada línea exige una decisión explícita, y al cerrar los ajustes se
// aplican sobre los activos.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CalendarCheck, Check, Loader2, Lock, Plus, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, MOTIVO_REVISION, Modal, RevisionLinea, RevisionRow, afRpc, fieldCls, money } from './shared';

export function RevisionVidaUtilAF() {
  const qc = useQueryClient();
  const [sel, setSel] = useState<RevisionRow | null>(null);
  const [abrir, setAbrir] = useState(false);
  const [form, setForm] = useState({ anio: new Date().getFullYear(), responsable: '', tope: 100 });

  const { data: rows = [], isFetching } = useQuery<RevisionRow[]>({
    queryKey: ['af-revisiones'], queryFn: () => afRpc<RevisionRow[]>('af_revisiones'),
  });
  const inval = () => { void qc.invalidateQueries({ queryKey: ['af-revisiones'] }); };

  const crear = useMutation({
    mutationFn: () => afRpc<{ propuestos: number }>('af_revision_abrir',
      { _anio: form.anio, _responsable: form.responsable || null, _tope: form.tope }),
    onSuccess: (r) => { toast.success(`Revisión abierta · ${r.propuestos} activo(s) propuestos`); setAbrir(false); inval(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (sel) return <PanelRevision rev={sel} onBack={() => { setSel(null); inval(); }} />;

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 text-xl font-semibold"><CalendarCheck className="h-5 w-5" /> Revisión de vida útil</h1>
        <Button onClick={() => setAbrir(true)}><Plus className="mr-1 h-4 w-4" /> Abrir revisión</Button>
      </div>
      <p className="max-w-3xl text-sm text-muted-foreground">
        La NIC 16 §51 pide revisar al menos en cada cierre la vida útil y el valor residual de los activos. El sistema propone a quién mirar —por evidencia, no por orden alfabético— y al cerrar la revisión aplica los ajustes y deja el acta.
      </p>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base">Revisiones ({rows.length}){isFetching && <Loader2 className="h-4 w-4 animate-spin text-primary" />}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Ejercicio</th><th className="py-2 pr-3">Responsable</th>
              <th className="py-2 pr-3 text-right">Revisados</th><th className="py-2 pr-3 text-right">Pendientes</th>
              <th className="py-2 pr-3 text-right">Ajustados</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin revisiones. Ábrela al cierre del ejercicio.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-medium">{r.anio}</td>
                  <td className="py-2 pr-3">{r.responsable ?? '—'}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{r.n_revisados}</td>
                  <td className={`py-2 pr-3 text-right tabular-nums ${r.pendientes > 0 ? 'font-semibold text-amber-600 dark:text-amber-400' : ''}`}>{r.pendientes}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{r.n_ajustados}</td>
                  <td className="py-2 pr-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs ${r.estado === 'abierta' ? 'bg-pendiente-soft text-pendiente' : 'bg-estable-soft text-estable'}`}>{r.estado}</span>
                    {r.cerrada_at && <span className="ml-2 text-xs text-muted-foreground">{String(r.cerrada_at).slice(0, 10)}{r.cerrada_por ? ` · ${r.cerrada_por}` : ''}</span>}
                  </td>
                  <td className="py-2 text-right"><Button size="sm" variant="outline" onClick={() => setSel(r)}>Abrir</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abrir && (
        <Modal title="Abrir revisión de vida útil" onClose={() => setAbrir(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Ejercicio *"><input type="number" className={fieldCls} value={form.anio} onChange={(e) => setForm({ ...form, anio: Number(e.target.value) })} /></Campo>
              <Campo label="Cuántos proponer"><input type="number" min="1" max="500" className={fieldCls} value={form.tope} onChange={(e) => setForm({ ...form, tope: Number(e.target.value) })} /></Campo>
            </div>
            <Campo label="Responsable de la revisión"><input className={fieldCls} value={form.responsable} onChange={(e) => setForm({ ...form, responsable: e.target.value })} placeholder="Nombre de quien firma" /></Campo>
            <p className="text-xs text-muted-foreground">
              Se proponen primero los activos con evidencia —totalmente depreciados y en uso, vida vencida, con indicio de deterioro abierto— y después los de mayor valor en libros, que son los que mueven la aguja.
            </p>
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setAbrir(false)}>Cancelar</Button>
              <Button isLoading={crear.isPending} onClick={() => crear.mutate()}>Abrir</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ------------------------------------------------------------- el panel ----
function PanelRevision({ rev, onBack }: { rev: RevisionRow; onBack: () => void }) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const abierta = rev.estado === 'abierta';
  const [editar, setEditar] = useState<RevisionLinea | null>(null);
  const [dec, setDec] = useState({ vida: '', residual: '', justificacion: '' });

  const { data: lineas = [], isFetching } = useQuery<RevisionLinea[]>({
    queryKey: ['af-revision-det', rev.id],
    queryFn: () => afRpc<RevisionLinea[]>('af_revision_detalle', { _revision_id: rev.id }),
  });
  const inval = () => {
    void qc.invalidateQueries({ queryKey: ['af-revision-det', rev.id] });
    void qc.invalidateQueries({ queryKey: ['af-revisiones'] });
  };

  const decidir = useMutation({
    mutationFn: (v: { id: string; decision: string; vida?: number | null; residual?: number | null; just?: string | null }) =>
      afRpc('af_revision_decidir', {
        _linea_id: v.id, _decision: v.decision,
        _vida_nueva: v.vida ?? null, _residual_nuevo: v.residual ?? null, _justificacion: v.just ?? null,
      }),
    onSuccess: () => { toast.success('Decisión registrada'); setEditar(null); inval(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const cerrar = useMutation({
    mutationFn: () => afRpc<{ ajustados: number }>('af_revision_cerrar', { _revision_id: rev.id }),
    onSuccess: (r) => { toast.success(`Revisión cerrada · ${r.ajustados} activo(s) ajustados`); inval(); onBack(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const pendientes = lineas.filter((l) => l.decision === 'pendiente').length;

  return (
    <div className="space-y-4 p-6">
      {dlg.ui}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" /> Volver
        </button>
        {abierta && (
          <Button size="sm" variant="outline" disabled={pendientes > 0}
            title={pendientes > 0 ? `Faltan ${pendientes} activo(s) por decidir` : 'Aplica los ajustes y cierra el acta'}
            onClick={async () => { if (await dlg.confirmar('¿Cerrar la revisión?', { detalle: 'Los ajustes se aplicarán sobre los activos.' })) cerrar.mutate(); }}>
            <Lock className="mr-1 h-4 w-4" /> Cerrar y aplicar
          </Button>
        )}
      </div>

      <h1 className="text-lg font-semibold">
        Revisión {rev.anio}{rev.responsable ? ` · ${rev.responsable}` : ''}
        <span className={`ml-2 rounded-full px-2 py-0.5 text-xs ${abierta ? 'bg-pendiente-soft text-pendiente' : 'bg-estable-soft text-estable'}`}>{rev.estado}</span>
      </h1>

      {abierta && pendientes > 0 && (
        <p className="text-sm text-amber-700 dark:text-amber-400">
          Faltan <b>{pendientes}</b> activo(s) por decidir. Un acta con renglones en blanco no prueba que se revisó nada, así que el cierre está bloqueado hasta resolverlos.
        </p>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base">Activos propuestos ({lineas.length}){isFetching && <Loader2 className="h-4 w-4 animate-spin text-primary" />}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Placa</th><th className="py-2 pr-3">Activo</th>
              <th className="py-2 pr-3">Por qué se propone</th>
              <th className="py-2 pr-3 text-right">Vida (meses)</th><th className="py-2 pr-3 text-right">Residual</th>
              <th className="py-2 pr-3 text-right">Valor en libros</th>
              <th className="py-2 pr-3">Decisión</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {lineas.map((l) => (
                <tr key={l.id} className="border-b last:border-0">
                  <td className="py-2 pr-3 font-mono">{l.placa}</td>
                  <td className="py-2 pr-3">{l.activo}<div className="text-xs text-muted-foreground">{l.categoria}</div></td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground">{MOTIVO_REVISION[l.motivo] ?? l.motivo}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">
                    {l.vida_anterior ?? '—'}
                    {l.vida_nueva != null && <span className="ml-1 font-semibold text-primary">→ {l.vida_nueva}</span>}
                  </td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">
                    {money(l.residual_anterior ?? 0)}
                    {l.residual_nuevo != null && <span className="ml-1 font-semibold text-primary">→ {money(l.residual_nuevo)}</span>}
                  </td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(l.libros_anterior)}</td>
                  <td className="py-2 pr-3">
                    {l.decision === 'pendiente'
                      ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">pendiente</span>
                      : l.decision === 'mantener'
                        ? <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Check className="h-3 w-3" /> se mantiene</span>
                        : <span className="inline-flex items-center gap-1 text-xs text-primary" title={l.justificacion ?? ''}><SlidersHorizontal className="h-3 w-3" /> ajustado</span>}
                    {l.revisado_por && <div className="text-[11px] text-muted-foreground">{l.revisado_por}</div>}
                  </td>
                  <td className="py-2 text-right whitespace-nowrap">
                    {abierta && (
                      <div className="inline-flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => decidir.mutate({ id: l.id, decision: 'mantener' })}>Mantener</Button>
                        <Button size="sm" variant="outline" onClick={() => { setEditar(l); setDec({ vida: String(l.vida_anterior ?? ''), residual: String(l.residual_anterior ?? ''), justificacion: '' }); }}>Ajustar</Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {editar && (
        <Modal title={`Ajustar ${editar.placa}`} onClose={() => setEditar(null)}>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{editar.activo}</p>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Vida útil nueva (meses)"><input type="number" min="1" className={fieldCls} value={dec.vida} onChange={(e) => setDec({ ...dec, vida: e.target.value })} /></Campo>
              <Campo label="Valor residual nuevo"><input type="number" step="0.01" min="0" className={fieldCls} value={dec.residual} onChange={(e) => setDec({ ...dec, residual: e.target.value })} /></Campo>
            </div>
            <Campo label="Justificación * (mínimo 10 caracteres)">
              <textarea className={`${fieldCls} h-20`} value={dec.justificacion}
                onChange={(e) => setDec({ ...dec, justificacion: e.target.value })}
                placeholder="Por qué cambia la estimación: informe técnico, certificación del fabricante, uso real…" />
            </Campo>
            <p className="text-xs text-muted-foreground">
              Es lo que se revela en notas (NIC 8 §39), y el cambio es <b>prospectivo</b>: la depreciación ya registrada no se toca; el saldo pendiente se reparte sobre la vida que quede.
            </p>
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setEditar(null)}>Cancelar</Button>
              <Button isLoading={decidir.isPending}
                disabled={dec.justificacion.trim().length < 10 || (!dec.vida && !dec.residual)}
                onClick={() => decidir.mutate({
                  id: editar.id, decision: 'ajustar',
                  vida: dec.vida ? Number(dec.vida) : null,
                  residual: dec.residual !== '' ? Number(dec.residual) : null,
                  just: dec.justificacion.trim(),
                })}>Guardar ajuste</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
